import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';

const PrivacyPolicyPage: FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      window.close();
    }
  };

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto">
      <div className="flex flex-col gap-6 w-full mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/assets/logo.png" alt="CareTailor" className="h-10 w-auto" />
        </div>
        {/* Header */}
        <div className="relative flex items-center justify-center">
          <Button
            color="ghost"
            shape="circle"
            className="absolute left-0 w-12 h-12 bg-black hover:bg-black text-white shadow-sm grid place-items-center rounded-xl"
            onClick={handleBack}
          >
            <img src="/assets/arrowLeft.svg" alt="Back" style={{ width: '16.5px', height: '15px' }} />
          </Button>
          <h1 className="text-lg font-semibold text-black">Privacy Policy</h1>
        </div>

        {/* Content */}
        <div className="bg-[#F6F6F4] rounded-[20px] shadow-sm p-4 md:p-6 lg:p-8 leading-relaxed text-sm md:text-base text-gray-800 space-y-6">
          <div className="space-y-1">
            <p className="font-semibold text-black">CareTailor Ltd</p>
            <p className="text-gray-500 text-sm">Last updated: 29/03/2026</p>
          </div>

          <p>
            This Privacy Policy explains how CareTailor Ltd (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects,
            uses and protects your personal data when you use the CareTailor platform. CareTailor Ltd is a company
            registered in England and Wales (company number 16534918, registered office 20 Wenlock Road, London,
            England, N1 7GU) and is the data controller for the personal data we process.
          </p>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">1. Information we collect</h2>
            <p className="font-medium">Information you give us:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Account details (name, email address, password)</li>
              <li>Profile information (photo, bio, care preferences, postcode)</li>
              <li>For carers: care types, skills, hourly rate, service area, availability and DBS / insurance details</li>
              <li>Messages you send through the platform</li>
              <li>Bookings, intro calls and any documents you upload</li>
            </ul>
            <p className="font-medium">Information we collect automatically:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Technical data such as device, browser and IP address</li>
              <li>Usage data about how you interact with the platform</li>
              <li>Cookies and similar technologies (see section 7)</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">2. How we use your information</h2>
            <p>We use your personal data to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Create and manage your account</li>
              <li>Match families with carers based on location, care needs and availability</li>
              <li>Enable messaging, bookings and intro calls between users</li>
              <li>Carry out limited safety checks (DBS, proof of insurance)</li>
              <li>Send you service-related communications</li>
              <li>Improve the platform and our services</li>
              <li>Comply with our legal obligations</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">3. Legal bases for processing</h2>
            <p>Under UK GDPR, we rely on the following legal bases:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                <span className="font-medium">Contract</span> – to provide the platform and the services you sign up for
              </li>
              <li>
                <span className="font-medium">Legitimate interests</span> – to operate, improve and secure the platform
              </li>
              <li>
                <span className="font-medium">Consent</span> – where required, for example for optional marketing
                communications
              </li>
              <li>
                <span className="font-medium">Legal obligation</span> – where we are required by law to process certain
                data
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">4. Sharing your information</h2>
            <p>We share personal data only where necessary, including with:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>
                Other users of the platform – for example, your profile and contact details are visible to people you
                connect with
              </li>
              <li>
                Service providers acting on our behalf (for example hosting, email, identity and DBS check providers)
              </li>
              <li>Authorities or regulators where required by law or to protect safety</li>
            </ul>
            <p>We do not sell your personal data.</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">5. International transfers</h2>
            <p>
              Some of our service providers may be located outside the UK or the European Economic Area. Where this is
              the case, we put appropriate safeguards in place (such as Standard Contractual Clauses) to protect your
              personal data.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">6. How long we keep your data</h2>
            <p>
              We keep personal data for as long as you have an account with us and for a reasonable period afterwards
              to meet our legal, accounting and reporting obligations, resolve disputes and enforce our agreements.
            </p>
            <p>You can ask us to delete your account at any time from the Settings page.</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">7. Cookies</h2>
            <p>
              We use a small number of essential cookies to keep you signed in and to run the platform. We may also use
              analytics cookies to understand how the platform is used; these are only set with your consent where
              required.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">8. Your rights</h2>
            <p>Under UK GDPR you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Access the personal data we hold about you</li>
              <li>Ask us to correct inaccurate or incomplete data</li>
              <li>Ask us to delete your personal data</li>
              <li>Restrict or object to certain processing</li>
              <li>Request portability of your data</li>
              <li>Withdraw consent at any time, where we rely on consent</li>
            </ul>
            <p>
              To exercise these rights please email{' '}
              <a href="mailto:support@caretailor.co.uk" className="text-primary underline">
                support@caretailor.co.uk
              </a>
              .
            </p>
            <p>
              You also have the right to lodge a complaint with the UK Information Commissioner&apos;s Office (ICO) at{' '}
              <a
                href="https://ico.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                ico.org.uk
              </a>
              .
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">9. Security</h2>
            <p>
              We take reasonable technical and organisational measures to protect your personal data, but no online
              service can be guaranteed to be 100% secure. Please keep your password confidential.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">10. Children</h2>
            <p>The CareTailor platform is not intended for anyone under 18 and we do not knowingly collect data from children.</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">11. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will update the &quot;Last updated&quot; date at
              the top of this page and, where appropriate, notify you of significant changes.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-black">12. Contact</h2>
            <p>
              If you have any questions about this Privacy Policy or how we handle your personal data, please contact
              us at{' '}
              <a href="mailto:support@caretailor.co.uk" className="text-primary underline">
                support@caretailor.co.uk
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
